import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import { StatusChip } from '@/components/common/StatusChip';
import { formatBytes, formatRelative } from '@/lib/format';
import { isActive, statusLabel, statusTone } from '@/features/migrations/status';
import type { Migration } from '@/features/migrations/types';

export function MigrationProgressBar({ m }: { m: Migration }) {
  const pct = m.progress.percent;
  const tone = m.status === 'failed' ? 'bg-danger' : m.status === 'completed_with_warnings' ? 'bg-warning' : m.status === 'cancelled' ? 'bg-fg-subtle' : 'bg-brand';
  return (
    <div className="w-44">
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-raised" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Migration progress">
        <div className={`h-full ${tone} ${isActive(m) ? 'transition-all' : ''}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1 text-xs text-fg-muted">
        {m.progress.messagesCopied.toLocaleString()} copied · {pct}%{m.progress.bytesCopied > 0 ? ` · ${formatBytes(m.progress.bytesCopied)}` : ''}
      </p>
    </div>
  );
}

export function MigrationsTable({ migrations, isLoading, isError, onRetry, onOpen }: {
  migrations: Migration[]; isLoading: boolean; isError?: boolean; onRetry?: () => void; onOpen: (m: Migration) => void;
}) {
  const columns = useMemo<ColumnDef<Migration, unknown>[]>(() => [
    {
      id: 'mailbox', header: 'Mailbox',
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-fg">{row.original.destinationAddress ?? 'Deleted mailbox'}</p>
          <p className="truncate text-xs text-fg-muted">from {row.original.source.username} @ {row.original.source.host}</p>
        </div>
      ),
    },
    { id: 'status', header: 'Status', cell: ({ row }) => <StatusChip tone={statusTone(row.original.status)}>{statusLabel(row.original)}</StatusChip> },
    { id: 'progress', header: 'Progress', cell: ({ row }) => <MigrationProgressBar m={row.original} /> },
    {
      id: 'folder', header: 'Now',
      cell: ({ row }) => {
        const m = row.original;
        if (isActive(m)) return <span className="text-sm text-fg-muted">{m.currentFolder ?? (m.status === 'queued' ? 'Waiting for a worker' : 'Connecting')}</span>;
        if (m.error) return <span className="line-clamp-2 text-sm text-danger">{m.error.summary}</span>;
        return <span className="text-sm text-fg-muted">{m.progress.foldersDone}/{m.progress.foldersTotal} folders</span>;
      },
    },
    { id: 'created', header: 'Started', cell: ({ row }) => <span className="text-sm text-fg-muted">{formatRelative(row.original.createdAt)}</span> },
  ], []);

  return (
    <DataTable columns={columns} data={migrations} getRowId={(m) => m.id} isLoading={isLoading} isError={isError} onRetry={onRetry}
      onRowClick={onOpen} emptyMessage="No migrations yet." skeletonRows={4} />
  );
}
